// 登录页：飞书 OAuth、本地账号密码、统一入口（按 /api/auth/methods 运行时装配）。
// 版式、空提交、提交中锁按钮、失败提示都在 @bf/ui 的 LoginLayout / LoginMethods（LeapWorldTwin-1000）：
// 本页只决定开哪些登录方式、怎么提交。

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, type AuthMethods } from "../../lib/auth";
import { api } from "../../lib/api";
import { branding } from "../../../shared/branding";
import { Alert, LoginLayout, LoginMethods } from "@bf/ui";

export function LoginPage() {
  const { login, loginLocal, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [feishuError, setFeishuError] = useState<string | null>(null);
  const authError = params.get("auth_error");
  const redirect = params.get("redirect") ?? "/";
  const safeRedirect = redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/";
  const binding = params.get("center_link") === "1";

  useEffect(() => {
    void api.get<AuthMethods>("/api/auth/methods", { skipAuthRedirect: true })
      .then(setMethods)
      .catch(() => setMethods({ providers: ["feishu"], feishuAuthEnabled: true }));
  }, []);

  useEffect(() => {
    if (user) navigate(user.mustChangePassword ? "/change-password" : safeRedirect, { replace: true });
  }, [user, navigate, safeRedirect]);

  const startFeishu = async () => {
    setFeishuError(null);
    try {
      await login(safeRedirect);
    } catch {
      setFeishuError("无法打开飞书登录，请稍后重试");
    }
  };

  const signIn = async (values: { username: string; password: string }) => {
    if (binding) {
      const result = await api.post<{ user: { mustChangePassword?: boolean }; csrfToken: string }>(
        "/api/auth/center/link-password",
        { email: values.username, password: values.password },
        { skipAuthRedirect: true },
      );
      window.location.assign(result.user?.mustChangePassword ? "/change-password" : safeRedirect);
      return;
    }
    const result = await loginLocal(values.username, values.password);
    navigate(result.mustChangePassword ? "/change-password" : safeRedirect, { replace: true });
  };

  const showCenter = Boolean(methods?.providers.includes("center"));
  const showFeishu = Boolean(methods?.feishuAuthEnabled && methods.providers.includes("feishu"));
  const showLocal = Boolean(methods?.providers.includes("local"));
  const available: Array<"center" | "feishu" | "local"> = binding
    ? ["local"]
    : [...(showCenter ? ["center" as const] : []), ...(showFeishu ? ["feishu" as const] : []), ...(showLocal ? ["local" as const] : [])];

  return (
    <LoginLayout
      branding={branding}
      binding={binding}
      loading={methods === null}
      methods={available}
      authError={authError}
      notice={feishuError ? <Alert tone="danger" title="登录没有成功">{feishuError}</Alert> : null}
    >
      <LoginMethods
        methods={available}
        onStart={(method) => (method === "center" ? window.location.assign("/api/auth/center/authorize") : startFeishu())}
        local={available.includes("local") ? {
          binding,
          onSubmit: signIn,
          onBack: () => { const next = new URLSearchParams(params); next.delete("center_link"); setParams(next, { replace: true }); },
          disableUntilFilled: true,
          ...(binding ? { usernameLabel: "积分账号邮箱", usernameType: "email" as const } : { usernameLabel: "账号", usernamePlaceholder: "管理员邮箱" }),
        } : undefined}
      />
      {!binding && !showLocal && showFeishu && !showCenter ? (
        <p className="m-0 text-center text-xs text-[var(--color-muted)]">仅支持飞书账号登录（扫码或免登）</p>
      ) : null}
    </LoginLayout>
  );
}
